import React, { useState, useEffect } from 'react';
import { Button, MarkdownRenderer, Spinner, Textarea, Toaster } from '../common';
import { useAiSummary, useUpdateAiSummary } from '../../hooks/useProfile.ts';
import { useApproveAISummary } from '../../hooks/useOnboarding.ts';
import { FloppyDisk, Edit, Xmark, Check } from 'iconoir-react';
import toast from 'react-hot-toast';

const AiSummary = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [isApproved, setIsApproved] = useState(false);

  const { data, isLoading, refetch } = useAiSummary();
  const { mutateAsync: approveAISummary, isPending: isSaving } =
    useApproveAISummary();
  const { mutateAsync: updateSummary, isPending: isUpdating } =
    useUpdateAiSummary();

  // BUG FIX: Log data structure for debugging
  useEffect(() => {
    if (data && !isLoading) {
      console.log('[AiSummary] Data structure:', data);
      console.log('[AiSummary] Summary field:', data?.result?.summary);
      console.log('[AiSummary] Full result:', data?.result);
    }
  }, [data, isLoading]);

  // BUG FIX: Extract summary with fallback for different response structures
  const getSummaryText = () => {
    if (!data?.result) return null;

    // Try different possible field names
    return (
      data.result.summary ||
      data.result.content ||
      data.result.persona ||
      data.result.text ||
      data.result.description ||
      null
    );
  };

  const summaryText = getSummaryText();

  const handleStartEdit = () => {
    setEditedSummary(summaryText || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedSummary('');
  };

  const handleSaveEdit = async () => {
    // Confirmation — saving profile triggers re-matching
    const confirmed = window.confirm(
      'Saving changes will refresh your matches based on your updated profile. Continue?'
    );
    if (!confirmed) return;

    try {
      await updateSummary({
        id: data?.result?.id || '',
        summary: editedSummary,
      }, {
        onSuccess: () => {
          toast.custom(t => (
            <Toaster
              message='Profile updated, your matches are being refreshed'
              type='success'
              id={t.id}
            />
          ));
          setIsEditing(false);
          refetch();
        },
        onError: (error: any) => {
          toast.custom(t => (
            <Toaster message={error?.message || 'Failed to update'} type='error' id={t.id} />
          ));
        },
      });
    } catch (err) {
      console.error('Failed to save edit:', err);
    }
  };

  const handleSaveAISummary = async () => {
    try {
      await approveAISummary(data?.result?.id || '');
      // Show success state
      setIsApproved(true);
      toast.custom(t => (
        <Toaster
          message='AI summary approved and saved'
          type='success'
          id={t.id}
        />
      ));
      // Reset approval state after 5 seconds to allow re-approval if needed
      setTimeout(() => setIsApproved(false), 5000);
    } catch (error: any) {
      toast.custom(t => (
        <Toaster
          message={error?.message || 'Failed to approve summary'}
          type='error'
          id={t.id}
        />
      ));
    }
  };

  // Reset approved state when data changes (e.g., after edit)
  useEffect(() => {
    setIsApproved(false);
  }, [summaryText]);

  return (
    <div className='max-w-[45.5rem] flex flex-col'>
      {isLoading ? (
        <div className='flex justify-center items-center min-h-[20rem]'>
          <Spinner size='sm' color='primary' />
        </div>
      ) : (
        <>
          {/* Header section - always visible, never overlapped */}
          <div className='flex items-center justify-between mb-2 flex-shrink-0'>
            <h5 className='text-heading-500 !font-medium'>AI Summary</h5>
            {summaryText && !isEditing && (
              <Button
                size='sm'
                variant='outline'
                onClick={handleStartEdit}
              >
                <Edit className='w-4 h-4' />
                Edit
              </Button>
            )}
          </div>

          {summaryText ? (
            <div className='flex flex-col flex-grow'>
              {/* Description - only show when NOT editing to prevent overlap */}
              {!isEditing && (
                <p className='mb-4 text-body-400 text-sm flex-shrink-0'>
                  This is how your AI agent describes you to potential matches. You can edit it to better reflect your professional identity.
                </p>
              )}

              {isEditing ? (
                <div className='flex flex-col gap-4'>
                  <p className='text-body-400 text-sm'>
                    Your AI summary is used to find your matches. Editing it will refresh your matches based on your changes.
                  </p>
                  {/* BUG-044 FIX: Use textareaWrapperClassName to override fixed h-12 height,
                      ensuring textarea properly expands and buttons remain accessible */}
                  <Textarea
                    wrapperClassName=''
                    textareaWrapperClassName='!h-auto min-h-[20rem]'
                    textareaClassName='resize-none min-h-[18rem] font-mono text-sm leading-relaxed'
                    value={editedSummary}
                    onChange={(e) => setEditedSummary(e.target.value)}
                    placeholder='Edit your AI summary...'
                  />
                  <div className='flex flex-col sm:flex-row justify-end gap-2 mt-2'>
                    <Button
                      className='w-full sm:w-auto'
                      variant='outline'
                      onClick={handleCancelEdit}
                      disabled={isUpdating}
                    >
                      <Xmark className='w-5 h-5' />
                      Cancel
                    </Button>
                    <Button
                      color='dark'
                      className='w-full sm:w-auto'
                      onClick={handleSaveEdit}
                      isLoading={isUpdating}
                    >
                      <FloppyDisk className='w-6 h-6' />
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className='max-h-[calc(100dvh-28rem)] overflow-y-auto mb-3 bg-gray-50 rounded-lg p-4'>
                    <MarkdownRenderer markdown={summaryText || ''} />
                  </div>

                  <div className='flex flex-col sm:flex-row justify-end gap-2 flex-shrink-0'>
                    <Button
                      color={isApproved ? 'primary' : 'dark'}
                      className={`w-full sm:w-auto transition-all ${isApproved ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      onClick={handleSaveAISummary}
                      isLoading={isSaving}
                      disabled={isApproved}
                    >
                      {isApproved ? (
                        <>
                          <Check className='w-6 h-6' />
                          Approved
                        </>
                      ) : (
                        <>
                          <FloppyDisk className='w-6 h-6' />
                          Approve summary
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className='flex flex-col justify-center items-center min-h-[20rem] mt-6'>
              <p className='text-body-500 font-medium mb-4'>No AI summary found</p>
              <p className='text-body-400 text-sm text-center'>
                Your AI summary will be generated automatically after completing onboarding.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AiSummary;
